/**
 * DispositionEditor — the inline ADMIN/owner-correction form for ONE (AUO × zone)
 * cell (Slice 5 override loop). Admin-gated by the CALLER. Lets the owner set the
 * disposition (favourable/neutral/caution/critical → OK/TOL/NO), the VERBATIM
 * effect text, and the remedy. The same form handles a wrong-derived cell AND a
 * db-miss "No ruling yet" cell — the only difference is the starting values.
 *
 * SHARED PRIMITIVE (extracted 2026-06-30): originally lived inside AuoReviewPanel
 * (the left staff list). The eval-workspace right-pane AUO naming card reuses the
 * SAME editor so both surfaces stay in lockstep.
 *
 * SCOPE CHOOSER (Silas's design §3.1, "ask me each time"): when `withScopeChoice`
 * is set, Save splits into TWO explicit options — "This project only" (writes the
 * per-project subcollection) vs "All reports (global)" (the existing global
 * write). No silent sticky default — the choice is shown and required every time.
 * When `withScopeChoice` is false (legacy left list) the single global Save button
 * is kept, so that surface is byte-for-byte unchanged.
 *
 * Pure-white field, dark ink, border-only distinction (pure-white policy). The
 * literal `#fff`/`#1a1a1a` (== --ink) are hard fallbacks so typed text can never
 * go white-on-white if a var fails to resolve.
 */
import { useId, useState } from 'react';
import { Btn3 } from './ui';
import { OVERRIDE_DISPOSITIONS, type OverrideDisposition } from '../lib/auoOverrides';

const cell: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '6px 8px',
  fontSize: 12.5,
  fontFamily: 'inherit',
  color: 'var(--ink, #1a1a1a)',
  background: '#FFFFFF',
};

export type DispositionSavePatch = {
  disposition: OverrideDisposition;
  effect: string;
  management: string | null;
  /** which layer to write to — chosen on save (defaults to 'global' on the legacy
   *  single-button path). */
  scope: 'project' | 'global';
};

export default function DispositionEditor({
  zone,
  current,
  onCancel,
  onSave,
  withScopeChoice = false,
  projectAvailable = false,
}: {
  zone: string;
  current: {
    disposition: string | null;
    effect?: string | null;
    management?: string | null;
  };
  onCancel: () => void;
  onSave: (patch: DispositionSavePatch) => void;
  /** show the project-vs-global scope chooser on save (the workspace path). */
  withScopeChoice?: boolean;
  /** is a project in scope? false → "this project only" is disabled with a hint. */
  projectAvailable?: boolean;
}) {
  const startDisp: OverrideDisposition = (OVERRIDE_DISPOSITIONS as readonly string[]).includes(
    current.disposition || '',
  )
    ? (current.disposition as OverrideDisposition)
    : 'neutral';
  const [disposition, setDisposition] = useState<OverrideDisposition>(startDisp);
  const [effect, setEffect] = useState(current.effect || '');
  const [management, setManagement] = useState(current.management || '');

  const commit = (scope: 'project' | 'global') =>
    onSave({
      disposition,
      effect: effect.trim(),
      management: management.trim() || null,
      scope,
    });
  const canSave = !!effect.trim();
  // why-disabled reasons surfaced as VISIBLE inline text (+ aria-describedby), so
  // keyboard / screen-reader users get the reason — not buried in a `title` on a
  // disabled control (WCAG 3.3 / MEDIUM-2). The textarea is the leaf input.
  const effectHintId = useId();
  const projHintId = useId();
  const projectBlocked = withScopeChoice && !projectAvailable;

  return (
    <div
      data-disposition-editor={zone}
      onClick={(e) => e.stopPropagation()}
      style={{
        marginTop: 6,
        padding: 8,
        border: '1px solid var(--rule-strong)',
        borderRadius: 'var(--r-sm)',
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-mute)' }}>
          Correct {zone}:
        </span>
        <select
          style={{ ...cell }}
          value={disposition}
          onChange={(e) => setDisposition(e.target.value as OverrideDisposition)}
          aria-label="Corrected disposition"
        >
          {OVERRIDE_DISPOSITIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      <textarea
        style={{ ...cell, width: '100%', minHeight: 44, resize: 'vertical' }}
        value={effect}
        placeholder="Effect — the owner's verbatim ruling for this placement"
        onChange={(e) => setEffect(e.target.value)}
        aria-label="Corrected effect text"
      />
      <input
        style={{ ...cell, width: '100%' }}
        value={management}
        placeholder="Remedy (optional)"
        onChange={(e) => setManagement(e.target.value)}
        aria-label="Corrected remedy"
      />

      {/* VISIBLE why-disabled hints (reachable by keyboard + SR), associated to the
          save buttons via aria-describedby. */}
      {!canSave && (
        <p id={effectHintId} style={{ margin: 0, fontSize: 11, color: 'var(--crit, #b91c1c)' }}>
          Enter the effect text to save this correction.
        </p>
      )}
      {withScopeChoice && projectBlocked && (
        <p id={projHintId} style={{ margin: 0, fontSize: 11, color: 'var(--ink-mute)' }}>
          No project in scope — open this from a project to save a project-only correction.
        </p>
      )}

      {withScopeChoice ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
            Save this correction to
          </span>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Btn3 intent="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Btn3>
            <Btn3
              intent="ghost"
              size="sm"
              disabled={!canSave || !projectAvailable}
              aria-describedby={!canSave ? effectHintId : projectBlocked ? projHintId : undefined}
              onClick={() => commit('project')}
            >
              This project only
            </Btn3>
            <Btn3
              intent="ink"
              size="sm"
              disabled={!canSave}
              aria-describedby={!canSave ? effectHintId : undefined}
              onClick={() => commit('global')}
            >
              All reports (global)
            </Btn3>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn3 intent="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Btn3>
          <Btn3
            intent="ink"
            size="sm"
            disabled={!canSave}
            aria-describedby={!canSave ? effectHintId : undefined}
            onClick={() => commit('global')}
          >
            Save correction
          </Btn3>
        </div>
      )}
    </div>
  );
}
